import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ChevronLeft, Loader2, MapPin, Building2, AlertTriangle, Tag, Sparkles, CalendarClock, Check, Ticket,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { directoryApi, membersApi } from "@/lib/api/directory";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function ProviderDetailPage() {
    const { t, lang } = useI18n();
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [claimedIds, setClaimedIds] = useState(() => new Set());
    const [claimingId, setClaimingId] = useState(null);

    useEffect(() => {
        let active = true;
        directoryApi.providerDetail(id)
            .then((d) => { if (active) setData(d); })
            .catch((err) => active && toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message));
        return () => { active = false; };
    }, [id]);

    useEffect(() => {
        let active = true;
        membersApi.listClaims()
            .then((res) => {
                if (!active) return;
                const ids = new Set(
                    (res?.items || [])
                        .filter((c) => (c.status || "CLAIMED") !== "CANCELLED")
                        .map((c) => c.offer_id)
                );
                setClaimedIds(ids);
            })
            .catch(() => { /* not blocking */ });
        return () => { active = false; };
    }, []);

    const claimOffer = async (offerId) => {
        setClaimingId(offerId);
        try {
            await membersApi.claimOffer(offerId);
            setClaimedIds((prev) => new Set(prev).add(offerId));
            toast.success(t("offers.claimSuccess"));
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setClaimingId(null);
        }
    };

    if (!data) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]" data-testid="page-member-provider-loading">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    const p = data.provider;
    const description = lang === "ar"
        ? (p.description_ar || p.description_en)
        : (p.description_en || p.description_ar);

    return (
        <div className="max-w-5xl space-y-8 fade-up" data-testid="page-member-provider">
            <Link
                to="/member/home"
                className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
                data-testid="back-to-directory"
            >
                <ChevronLeft className="h-4 w-4 me-1" />
                {t("common.back")}
            </Link>

            <header className="surface-card p-6 sm:p-8">
                <div className="flex items-start gap-4">
                    <div className="icon-tile h-16 w-16 shrink-0 overflow-hidden">
                        {p.logo_url
                            ? <img src={p.logo_url} alt={p.corporate_name} className="h-full w-full object-cover" data-testid="provider-logo" />
                            : <Building2 className="h-8 w-8" />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <Badge variant="outline" className="mb-2 text-[11px]">{p.type}</Badge>
                        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
                            {p.corporate_name}
                        </h1>
                        {description && (
                            <p className="mt-2 text-sm text-muted-foreground max-w-3xl">{description}</p>
                        )}
                        {p.neighborhoods?.length > 0 && (
                            <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                                <MapPin className="h-3.5 w-3.5" />
                                {p.neighborhoods.map((n) => (
                                    <span key={n} className="px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                                        {n}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {data.categories?.length > 0 && (
                <section className="surface-card p-6">
                    <div className="flex items-center gap-2 mb-3">
                        <Tag className="h-4 w-4 text-primary" />
                        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                            {t("member.provider.categories")}
                        </h2>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {data.categories.map((c) => (
                            <span
                                key={c.id}
                                className="text-sm px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground"
                                data-testid={`provider-category-${c.id}`}
                            >
                                {lang === "ar" ? c.name_ar : c.name_en}
                            </span>
                        ))}
                    </div>
                </section>
            )}

            <section className="surface-card p-6" data-testid="provider-offers-section">
                <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                        {t("member.provider.activeOffers")}
                    </h2>
                </div>
                {data.offers && data.offers.length > 0 ? (
                    <div className="space-y-3">
                        {data.offers.map((offer) => {
                            const titleText = lang === "ar" ? (offer.title_ar || offer.title_en) : (offer.title_en || offer.title_ar);
                            const descText = lang === "ar" ? (offer.description_ar || offer.description_en) : (offer.description_en || offer.description_ar);
                            const termsText = lang === "ar" ? (offer.terms_ar || offer.terms_en) : (offer.terms_en || offer.terms_ar);
                            return (
                                <article key={offer.id} className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-secondary overflow-hidden" data-testid={`member-offer-${offer.id}`}>
                                    {offer.image_url && (
                                        <img src={offer.image_url} alt={titleText} className="w-full aspect-[16/9] object-cover" />
                                    )}
                                    <div className="flex items-start gap-4 p-5">
                                        <div className="text-primary shrink-0">
                                            {offer.discount_type === "PERCENTAGE" ? (
                                                <div className="text-3xl font-bold tracking-tight" dir="ltr">
                                                    {Math.round(offer.discount_value)}%
                                                </div>
                                            ) : (
                                                <div className="flex flex-col" dir="ltr">
                                                    <span className="text-2xl font-bold leading-none">{Math.round(offer.discount_value)}</span>
                                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("offers.egp")}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-base font-semibold">{titleText}</h3>
                                            {offer.discount_type === "FIXED_PACKAGE" && offer.original_price && (
                                                <p className="text-xs text-muted-foreground mt-0.5" dir="ltr">
                                                    {t("offers.was")} <span className="line-through">{Math.round(offer.original_price)} {t("offers.egp")}</span>
                                                </p>
                                            )}
                                            {descText && <p className="mt-2 text-sm">{descText}</p>}
                                            <p className="mt-2 text-xs text-muted-foreground" dir="ltr">
                                                {t("offers.validUntil", { date: offer.end_date })}
                                            </p>
                                            {termsText && (
                                                <details className="mt-2">
                                                    <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">{t("offers.terms")}</summary>
                                                    <p className="mt-1 text-xs text-muted-foreground">{termsText}</p>
                                                </details>
                                            )}
                                            <div className="mt-3">
                                                {claimedIds.has(offer.id) ? (
                                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 text-success px-3 py-1 text-xs font-semibold" data-testid={`offer-claimed-${offer.id}`}>
                                                        <Check className="h-3.5 w-3.5" />
                                                        {t("offers.claimed")}
                                                    </span>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => claimOffer(offer.id)}
                                                        disabled={claimingId === offer.id}
                                                        data-testid={`claim-offer-${offer.id}`}
                                                    >
                                                        {claimingId === offer.id ? (
                                                            <Loader2 className="h-4 w-4 animate-spin me-2" />
                                                        ) : (
                                                            <Ticket className="h-4 w-4 me-2" />
                                                        )}
                                                        {t("offers.claim")}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground">{t("offers.memberNoneActive")}</p>
                )}
            </section>

            <section className="surface-card p-6">
                <div className="flex items-center gap-2 mb-3">
                    <CalendarClock className="h-4 w-4 text-primary" />
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                        {t("member.provider.locations")}
                    </h2>
                </div>
                {data.locations?.length > 0 ? (
                    <div className="grid sm:grid-cols-2 gap-3">
                        {data.locations.map((loc) => (
                            <div key={loc.id} className="rounded-xl border border-border bg-card p-4" data-testid={`provider-location-${loc.id}`}>
                                <div className="flex items-start gap-3">
                                    <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold">
                                            {lang === "ar" ? loc.name_ar : loc.name_en}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {[loc.neighborhood, loc.city].filter(Boolean).join(", ")}
                                        </p>
                                        {loc.phone && (
                                            <p className="text-xs text-muted-foreground" dir="ltr">{loc.phone}</p>
                                        )}
                                        {loc.hours_text && (
                                            <p className="text-xs text-muted-foreground mt-1">{loc.hours_text}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground">{t("member.provider.locationsComingSoon", { n: p.branch_count })}</p>
                )}
            </section>

            {data.staff?.length > 0 && (
                <section className="surface-card p-6" data-testid="provider-staff-section">
                    <div className="flex items-center gap-2 mb-3">
                        <CalendarClock className="h-4 w-4 text-primary" />
                        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                            {t("member.provider.staff")}
                        </h2>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {data.staff.map((s) => (
                            <div key={s.id} className="rounded-xl border border-border bg-card p-4 flex items-start gap-3" data-testid={`provider-staff-${s.id}`}>
                                <div className="h-12 w-12 rounded-full overflow-hidden bg-muted shrink-0 ring-2 ring-secondary">
                                    {s.photo_url
                                        ? <img src={s.photo_url} alt={s.full_name_en} className="h-full w-full object-cover" />
                                        : <div className="h-full w-full flex items-center justify-center text-sm font-semibold text-muted-foreground">
                                            {(s.full_name_en || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
                                          </div>}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold truncate">
                                        {lang === "ar" ? s.full_name_ar : s.full_name_en}
                                    </p>
                                    <p className="text-xs text-muted-foreground truncate">
                                        {lang === "ar" ? (s.role_ar || s.role_en) : (s.role_en || s.role_ar)}
                                    </p>
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {(s.availability || []).map((slot, i) => {
                                            const loc = data.locations.find((l) => l.id === slot.location_id);
                                            return (
                                                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                                                    {loc ? (lang === "ar" ? loc.name_ar : loc.name_en) : "—"} · {(slot.days || []).join("/")} · {slot.from_time}–{slot.to_time}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <div className="flex justify-end">
                <Button
                    variant="outline"
                    onClick={() => toast.info(t("member.provider.reportComingSoon"))}
                    data-testid="provider-report-button"
                    className="text-destructive hover:bg-destructive/5"
                >
                    <AlertTriangle className="h-4 w-4 me-2" />
                    {t("member.provider.reportProblem")}
                </Button>
            </div>
        </div>
    );
}
